<template>
  <the-app-shell
    :backgroundColor="backgroundColor"
    :backgroundDarkColor="backgroundDarkColor"
    :showLoginButton="showLoginButton"
    :progress="progress"
    :isEndUser="isEndUser"
    @logoClick="logoClick"
  >
    <v-divider v-if="displayDivider" class="no-print" />
    <slot />
    <PartnerFooter
      v-if="partner && showFooter && !isHeadless"
      :partnerProp="partner"
      :color="color"
      class="d-none d-md-flex"
    />
  </the-app-shell>
</template>

<script lang="ts">
import PartnerFooter from "@/components/partner/PartnerFooter.vue";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { ConfigModule } from "@/store/modules/config";
import { PartnerModule } from "@/store/modules/partner";
import { ReportModule } from "@/store/modules/report.store";
import { UiModule } from "@/store/modules/ui.store";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import TheAppShell from "./TheAppShell.vue";

/**
 * The content of a view inside the chrome of the app.
 *
 * The chrome itself lives in `TheAppShell`: an app that mounts the shell above the routed view -
 * see `Portal.vue` - keeps its navigation while the user navigates, and the shell rendered here
 * turns into a plain wrapper. Apps that do not hoist it yet get their chrome from here as before.
 */
@Component({
  components: {
    PartnerFooter,
    TheAppShell
  }
})
export default class TheLayoutBase extends Vue {
  @Prop({ default: "white" })
  backgroundColor!: string;

  @Prop({ default: false })
  showLoginButton!: boolean;

  @Prop({ default: "grey darken-3" })
  backgroundDarkColor!: string;

  @Prop({ default: 0 })
  progress!: number;

  @Prop({ default: true })
  showFooter!: boolean;

  @Prop({ default: false })
  displayDivider!: boolean;

  @Prop({})
  headerLogo?: string;

  @Prop({ default: false })
  isEndUser!: boolean;

  get partner() {
    return PartnerModule.partner ?? ReportModule.partner;
  }

  get isHeadless() {
    return ConfigModule.headless;
  }

  get color() {
    return getDefaultPartnerColor();
  }

  logoClick() {
    this.$emit("logoClick", true);
  }

  /**
   * Hands the logo of the view to the app bar.
   *
   * The shell outlives the view when it is hoisted above the routed view, so the logo cannot be
   * passed to it as a prop. It is published in `mounted` and withdrawn in `destroyed`, which Vue
   * runs before the layout of the next view is mounted.
   */
  @Watch("headerLogo")
  onHeaderLogoChange() {
    UiModule.setHeaderLogo(this.headerLogo ?? "");
  }

  mounted() {
    UiModule.setHeaderLogo(this.headerLogo ?? "");
  }

  destroyed() {
    UiModule.setHeaderLogo("");
  }
}
</script>

<style>
.progressContainer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-left: 28px !important;
  padding-right: 28px !important;
}
</style>
